import type { Locale } from "../../shared/api/types";
import { chromeLocale } from "../../shared/lib/locale-context";

/**
 * Chrome strings of the notification bell, its panel and (stream N-W2) the full page (batch 17, notifications panel design
 * §1–§3). English for every user (ruling 2026-10-04); the Thai column is provisional and unreachable while `chromeLocale()`
 * returns "en", like every other `*Text.ts` dictionary. `{name}` placeholders are filled by `notificationText`.
 */
export type NotificationTextKey =
  | "heading"
  | "onlyUnread"
  | "openFullPage"
  | "moreActions"
  | "markAllIn"
  | "settings"
  | "tabDirect"
  | "tabWatching"
  | "tabsLabel"
  | "latest"
  | "older"
  | "showOlder"
  | "loadingOlder"
  | "loading"
  | "loadError"
  | "olderError"
  | "tryAgain"
  | "markAllError"
  | "emptyDirect"
  | "emptyWatchingNone"
  | "emptyWatchingSome"
  | "caughtUp"
  | "showAll"
  | "close"
  | "markRead"
  | "markUnread"
  | "unreadPrefix"
  | "bellName"
  | "bellAttention"
  | "bellAttentionOne"
  | "bellUnreadNotices"
  | "bellNothingNew"
  | "bellAttentionFailed"
  | "bellCountFailed"
  | "bellBothFailed"
  | "bellTip"
  | "attentionHeading"
  | "attentionOpenHome"
  | "attentionLoading"
  | "attentionLoadError"
  | "attentionNothing"
  | "attentionLines"
  | "mentionComment"
  | "mentionNote"
  | "assigned"
  | "returned"
  | "returnedDeployment"
  | "approval"
  | "devops"
  | "slaAtRisk"
  | "slaBreached"
  | "statusReminder"
  | "moved"
  | "roleYou_dev"
  | "roleYou_sa"
  | "roleYou_tester"
  | "roleEvery_dev"
  | "roleEvery_sa"
  | "roleEvery_tester"
  | "roleTag_dev"
  | "roleTag_sa"
  | "roleTag_tester"
  | "updated"
  | "commented"
  | "addedNote"
  | "changedStatus"
  | "changedAssignee"
  | "multipleComment"
  | "multiple"
  | "commentDeleted"
  | "tfgTeam"
  | "system"
  | "internalNote"
  | "markedRead"
  | "markedUnread"
  | "allMarkedOne"
  | "allMarkedMany"
  | "allMarkedTab"
  | "notMarked"
  | "justNow"
  | "minutesAgo"
  | "hoursAgo"
  | "daysAgo"
  | "pageSub"
  | "filters"
  | "mentionsOnly"
  | "eventOnly"
  | "removeFilter"
  | "emptyMentions"
  | "markAllMentions"
  | "allMentionsMarked"
  | "telegramLink"
  | "markAllRead"
  | "markAllTabName"
  | "nothingUnread"
  | "allMarkedNotices"
  | "settingsLine";

export const NOTIFICATION_TEXT: Record<Locale, Record<NotificationTextKey, string>> = {
  en: {
    heading: "Notifications",
    onlyUnread: "Only show unread",
    openFullPage: "Open notifications in full page",
    moreActions: "More notification actions",
    markAllIn: "Mark all in {tab} as read",
    settings: "Notification settings",
    tabDirect: "Direct",
    tabWatching: "Watching",
    tabsLabel: "Notification lists",
    latest: "Latest",
    older: "Older",
    showOlder: "Show older",
    loadingOlder: "Loading…",
    loading: "Loading notifications…",
    loadError: "Notifications could not be loaded.",
    olderError: "Older notifications could not be loaded.",
    tryAgain: "Try again",
    markAllError: "Not marked read.",
    emptyDirect: "No notifications yet. When someone mentions you, assigns you an item, returns one to you, needs your approval or names you as its developer, SA or tester, it shows here.",
    emptyWatchingNone: "You are not watching any items. Use Watch on an item page to follow its comments, status and assignee changes.",
    emptyWatchingSome: "No activity on the items you watch yet.",
    caughtUp: "You're all caught up.",
    showAll: "Show all",
    close: "Close",
    markRead: "Mark as read",
    markUnread: "Mark as unread",
    unreadPrefix: "Unread.",
    bellName: "Notifications",
    bellAttention: "{n} need your attention",
    bellAttentionOne: "1 needs your attention",
    bellUnreadNotices: "unread notifications",
    bellNothingNew: "nothing new",
    bellAttentionFailed: "needs attention count not available",
    bellCountFailed: "notification count not available",
    bellBothFailed: "counts not available",
    bellTip: "Notifications and the work waiting on you",
    attentionHeading: "Needs attention",
    attentionOpenHome: "Open Home",
    attentionLoading: "Loading what needs your attention…",
    attentionLoadError: "Needs attention could not be loaded.",
    attentionNothing: "Nothing is waiting on you.",
    attentionLines: "What needs your attention",
    mentionComment: "mentioned you in a comment",
    mentionNote: "mentioned you in an internal note",
    assigned: "assigned this to you",
    returned: "returned this to you",
    returnedDeployment: "returned your deployment for rework",
    approval: "needs your approval",
    devops: "sent this to the DevOps queue",
    slaAtRisk: "SLA at risk",
    slaBreached: "SLA breached",
    // Batch 26 R2: the Wednesday / Thursday 09:00 reminder to a Capt. whose project has not reported its week yet.
    statusReminder: "Weekly status of {project} not reported yet · due Thursday 23:59",
    // Batch 23 (GRF-71, UX-B23 §5): a move told me because the item names me for a role, or sent to every holder of the role.
    moved: "moved this",
    roleYou_dev: "you are the developer",
    roleYou_sa: "you are the SA",
    roleYou_tester: "you are the tester",
    roleEvery_dev: "sent to every developer, none is named",
    roleEvery_sa: "sent to every SA, none is named",
    roleEvery_tester: "sent to every tester, none is named",
    roleTag_dev: "As developer",
    roleTag_sa: "As SA",
    roleTag_tester: "As tester",
    updated: "updated this",
    commented: "commented",
    addedNote: "added an internal note",
    changedStatus: "changed the status",
    changedAssignee: "changed the assignee",
    multipleComment: "commented and made other changes",
    multiple: "made several changes",
    commentDeleted: "(comment deleted)",
    tfgTeam: "TFG team",
    system: "Giraffe",
    internalNote: "Internal note",
    markedRead: "Marked as read",
    markedUnread: "Marked as unread",
    allMarkedOne: "1 notification marked read",
    allMarkedMany: "{n} notifications marked read",
    allMarkedTab: "All notifications in {tab} marked read",
    notMarked: "Could not change the read state. Try again.",
    justNow: "just now",
    minutesAgo: "{n} minutes ago",
    hoursAgo: "{n} hours ago",
    daysAgo: "{n} days ago",
    pageSub: "What happened that concerns you, newest first",
    filters: "Filters",
    mentionsOnly: "Mentions only",
    eventOnly: "Only {type}",
    removeFilter: "Remove filter: {filter}",
    emptyMentions: "No mentions yet. When someone mentions you in a comment, it shows here.",
    markAllMentions: "Mark all mentions as read",
    allMentionsMarked: "All mentions marked read",
    telegramLink: "Telegram notifications",
    markAllRead: "Mark all read",
    markAllTabName: "Mark all {tab} notices as read",
    nothingUnread: "Nothing unread here",
    allMarkedNotices: "All {tab} notices marked as read",
    settingsLine: "Choose what reaches Telegram and e-mail:",
  },
  th: {
    heading: "การแจ้งเตือน",
    onlyUnread: "แสดงเฉพาะที่ยังไม่อ่าน",
    openFullPage: "เปิดการแจ้งเตือนแบบเต็มหน้า",
    moreActions: "การทำงานอื่นของการแจ้งเตือน",
    markAllIn: "ทำเครื่องหมายว่าอ่านแล้วทั้งหมดใน {tab}",
    settings: "ตั้งค่าการแจ้งเตือน",
    tabDirect: "ถึงฉัน",
    tabWatching: "ที่ติดตาม",
    tabsLabel: "รายการการแจ้งเตือน",
    latest: "ล่าสุด",
    older: "เก่ากว่า",
    showOlder: "แสดงที่เก่ากว่า",
    loadingOlder: "กำลังโหลด…",
    loading: "กำลังโหลดการแจ้งเตือน…",
    loadError: "โหลดการแจ้งเตือนไม่ได้",
    olderError: "โหลดการแจ้งเตือนที่เก่ากว่าไม่ได้",
    tryAgain: "ลองอีกครั้ง",
    markAllError: "ยังไม่ได้ทำเครื่องหมายว่าอ่านแล้ว",
    emptyDirect: "ยังไม่มีการแจ้งเตือน เมื่อมีคนกล่าวถึงคุณ มอบหมายงาน ส่งงานกลับ รอการอนุมัติจากคุณ หรือระบุคุณเป็นนักพัฒนา SA หรือผู้ทดสอบ จะแสดงที่นี่",
    emptyWatchingNone: "คุณยังไม่ได้ติดตามรายการใด ใช้ปุ่มติดตามในหน้ารายการเพื่อติดตามความคิดเห็น สถานะ และผู้รับผิดชอบ",
    emptyWatchingSome: "ยังไม่มีความเคลื่อนไหวในรายการที่คุณติดตาม",
    caughtUp: "คุณอ่านครบแล้ว",
    showAll: "แสดงทั้งหมด",
    close: "ปิด",
    markRead: "ทำเครื่องหมายว่าอ่านแล้ว",
    markUnread: "ทำเครื่องหมายว่ายังไม่อ่าน",
    unreadPrefix: "ยังไม่อ่าน",
    bellName: "การแจ้งเตือน",
    bellAttention: "{n} เรื่องรอคุณดำเนินการ",
    bellAttentionOne: "1 เรื่องรอคุณดำเนินการ",
    bellUnreadNotices: "มีการแจ้งเตือนที่ยังไม่อ่าน",
    bellNothingNew: "ไม่มีอะไรใหม่",
    bellAttentionFailed: "นับเรื่องที่รอคุณไม่ได้",
    bellCountFailed: "นับการแจ้งเตือนไม่ได้",
    bellBothFailed: "นับจำนวนไม่ได้",
    bellTip: "การแจ้งเตือนและงานที่รอคุณ",
    attentionHeading: "ต้องดำเนินการ",
    attentionOpenHome: "เปิดหน้า Home",
    attentionLoading: "กำลังโหลดเรื่องที่รอคุณ…",
    attentionLoadError: "โหลดเรื่องที่รอคุณไม่ได้",
    attentionNothing: "ไม่มีเรื่องที่รอคุณ",
    attentionLines: "เรื่องที่รอคุณ",
    mentionComment: "กล่าวถึงคุณในความคิดเห็น",
    mentionNote: "กล่าวถึงคุณในบันทึกภายใน",
    assigned: "มอบหมายรายการนี้ให้คุณ",
    returned: "ส่งรายการนี้กลับมาให้คุณ",
    returnedDeployment: "ส่งการติดตั้งของคุณกลับมาแก้ไข",
    approval: "รอการอนุมัติจากคุณ",
    devops: "ส่งรายการนี้เข้าคิว DevOps",
    slaAtRisk: "SLA ใกล้เกินกำหนด",
    slaBreached: "SLA เกินกำหนด",
    statusReminder: "โครงการ {project} ยังไม่ได้รายงานสถานะประจำสัปดาห์ · ครบกำหนดวันพฤหัสบดี 23:59 น.",
    moved: "ย้ายรายการนี้",
    roleYou_dev: "คุณเป็นนักพัฒนา",
    roleYou_sa: "คุณเป็น SA",
    roleYou_tester: "คุณเป็นผู้ทดสอบ",
    roleEvery_dev: "ส่งถึงนักพัฒนาทุกคน เพราะยังไม่ได้ระบุ",
    roleEvery_sa: "ส่งถึง SA ทุกคน เพราะยังไม่ได้ระบุ",
    roleEvery_tester: "ส่งถึงผู้ทดสอบทุกคน เพราะยังไม่ได้ระบุ",
    roleTag_dev: "ในฐานะนักพัฒนา",
    roleTag_sa: "ในฐานะ SA",
    roleTag_tester: "ในฐานะผู้ทดสอบ",
    updated: "ปรับปรุงรายการนี้",
    commented: "แสดงความคิดเห็น",
    addedNote: "เพิ่มบันทึกภายใน",
    changedStatus: "เปลี่ยนสถานะ",
    changedAssignee: "เปลี่ยนผู้รับผิดชอบ",
    multipleComment: "แสดงความคิดเห็นและเปลี่ยนแปลงอื่น",
    multiple: "เปลี่ยนแปลงหลายอย่าง",
    commentDeleted: "(ความคิดเห็นถูกลบ)",
    tfgTeam: "ทีม TFG",
    system: "Giraffe",
    internalNote: "บันทึกภายใน",
    markedRead: "ทำเครื่องหมายว่าอ่านแล้ว",
    markedUnread: "ทำเครื่องหมายว่ายังไม่อ่าน",
    allMarkedOne: "ทำเครื่องหมายว่าอ่านแล้ว 1 รายการ",
    allMarkedMany: "ทำเครื่องหมายว่าอ่านแล้ว {n} รายการ",
    allMarkedTab: "ทำเครื่องหมายว่าอ่านแล้วทั้งหมดใน {tab}",
    notMarked: "เปลี่ยนสถานะการอ่านไม่ได้ ลองอีกครั้ง",
    justNow: "เมื่อสักครู่",
    minutesAgo: "{n} นาทีที่แล้ว",
    hoursAgo: "{n} ชั่วโมงที่แล้ว",
    daysAgo: "{n} วันที่แล้ว",
    pageSub: "สิ่งที่เกิดขึ้นและเกี่ยวกับคุณ ล่าสุดก่อน",
    filters: "ตัวกรอง",
    mentionsOnly: "เฉพาะการกล่าวถึง",
    eventOnly: "เฉพาะ {type}",
    removeFilter: "นำตัวกรองออก: {filter}",
    emptyMentions: "ยังไม่มีการกล่าวถึง เมื่อมีคนกล่าวถึงคุณในความคิดเห็น จะแสดงที่นี่",
    markAllMentions: "ทำเครื่องหมายว่าอ่านแล้วทุกการกล่าวถึง",
    allMentionsMarked: "ทำเครื่องหมายว่าอ่านแล้วทุกการกล่าวถึง",
    telegramLink: "การแจ้งเตือนทาง Telegram",
    markAllRead: "อ่านแล้วทั้งหมด",
    markAllTabName: "ทำเครื่องหมายว่าอ่านแล้วทั้งหมดใน {tab}",
    nothingUnread: "ไม่มีรายการที่ยังไม่อ่าน",
    allMarkedNotices: "ทำเครื่องหมายว่าอ่านแล้วทั้งหมดใน {tab}",
    settingsLine: "เลือกสิ่งที่ส่งไป Telegram และอีเมล:",
  },
};

/** One chrome string, `{name}` placeholders filled from `values`. */
export function notificationText(key: NotificationTextKey, values: Record<string, string | number> = {}, locale?: Locale): string {
  const template = NOTIFICATION_TEXT[chromeLocale(locale)][key];
  return template.replace(/\{([a-z]+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match));
}
